import { useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Empty, Flex, Input, Modal, Space, Table, Typography } from 'antd'
import {
  SalesCodeComplexityContext,
  SET_SHOW_HIDE_DETAILS_POPUP,
} from './context/context'
import { getRequest, postRequest } from '../../../services/base'
import { NavContext } from '../../../context/global_context'
import { downloadErrorLogs, downloadFile } from '../../../utils/common'
import RecordsCount from '../../common/RecordsCount'
import { useIntl } from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const SalesCodeDetails = () => {
  const { vehicle } = useParams()
  const intl = useIntl()
  const [salesCodeState, salesCodeDispatch] = useContext(SalesCodeComplexityContext)
  const [state, dispatch] = useContext(NavContext)
  const [modal, contextHolder] = Modal.useModal()
  const [salesCodeDetails, setSalesCodeDetails] = useState({})
  const [salesCodeDetailsList, setSalesCodeDetailsList] = useState([])
  const [salesCodeSearch, setSalesCodeSearch] = useState('')
  const [partNumberSearch, setPartNumberSearch] = useState('')

  const show = salesCodeState?.showDetailsPopup?.id ? true : false

  useEffect(() => {
    if (salesCodeState?.showDetailsPopup?.id) {
      setSalesCodeSearch('')
      setPartNumberSearch('')
      getSalesCodeComplexityDetails(salesCodeState?.showDetailsPopup?.id)
    }
  }, [salesCodeState?.showDetailsPopup?.id])

  const getSalesCodeComplexityDetails = async (id) => {
    dispatch({
      type: 'loader',
      data: { caption: intl.formatMessage({ id: 'salesCodeComplexity.fetchingDetails' }), showLoader: true },
      notification: {},
    })
    let res = await getRequest(
      `/api/plugin/sales_code/sales-code-details/${id}/?vehicle=${vehicle}`
    )
    dispatch({ type: 'loader', data: {}, notification: {} })

    // A 'loader' action resets `notification`, so notify after it, not before.
    if (res?.status === false) {
      notifyError(
        dispatch,
        intl.formatMessage({ id: 'salesCodeComplexity.loadError' })
      )
      return
    }

    setSalesCodeDetails(res.data)
    setSalesCodeDetailsList(res.data.sales_code_details)
  }

  const filteredList = salesCodeDetailsList.filter((row) => {
    const matchesSalesCode = salesCodeSearch
      ? row.sales_code?.toLowerCase().includes(salesCodeSearch.toLowerCase())
      : true
    const matchesPartNumber = partNumberSearch
      ? row.part_number?.toLowerCase().includes(partNumberSearch.toLowerCase())
      : true
    return matchesSalesCode && matchesPartNumber
  })

  const validateComplexity = async () => {
    modal.confirm({
      title: intl.formatMessage({ id: 'salesCodeComplexity.validationConfirmation' }),
      content: intl.formatMessage({ id: 'salesCodeComplexity.validationContent' }),
      centered: true,
      maskClosable: true,
      okText: intl.formatMessage({ id: 'salesCodeComplexity.validate' }),
      cancelText: intl.formatMessage({ id: 'forms.cancel' }),
      onOk: async () => {
        dispatch({
          type: 'loader',
          data: { caption: intl.formatMessage({ id: 'salesCodeComplexity.fetchingDetails' }), showLoader: true },
          notification: {},
        })
        let res = await postRequest(
          `/api/plugin/sales_code/validate-sales-code-complexity/${salesCodeState?.showDetailsPopup?.id}/${vehicle}/?vehicle=${vehicle}`
        )
        if (res?.status) {
          dispatch({
            type: 'notification',
            data: { notification_type: 'success', msg: intl.formatMessage({ id: 'salesCodeComplexity.validationDone' }), loader: true },
          })
        } else {
          dispatch({
            type: 'notification',
            data: { notification_type: 'error', msg: intl.formatMessage({ id: 'salesCodeComplexity.validationError' }), loader: true },
          })
        }
      },
    })
  }

  const columns = useMemo(() => [
    {
      title: '#',
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: (
        <Input
          size="medium"
          placeholder={intl.formatMessage({ id: 'salesCodeComplexity.searchSalesCodePlaceholder' })}
          value={salesCodeSearch}
          onChange={(e) => setSalesCodeSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      dataIndex: 'sales_code',
      ellipsis: { showTitle: false },
      render: (text) => <span title={text}>{text}</span>,
    },
    {
      title: (
        <Input
          size="medium"
          placeholder={intl.formatMessage({ id: 'salesCodeComplexity.searchPartNumberPlaceholder' })}
          value={partNumberSearch}
          onChange={(e) => setPartNumberSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      ),
      dataIndex: 'part_number',
      ellipsis: { showTitle: false },
      render: (text) => <span title={text}>{text}</span>,
    },
  ], [salesCodeSearch, partNumberSearch, intl])

  const dataSource = useMemo(() => filteredList.map((item, index) => ({
    key: index,
    sales_code: item.sales_code,
    part_number: item.part_number,
  })), [filteredList])

  return (
    <Modal
      title={
        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
          <span>{intl.formatMessage({ id: 'sidebar.salesCodeComplexity' })}</span>
        </Space>
      }
      open={show}
      centered={true}
      onCancel={() => salesCodeDispatch({ type: SET_SHOW_HIDE_DETAILS_POPUP, data: {} })}
      width="75%"
      styles={{
        body: {
          maxHeight: '75vh',
          overflowY: 'hidden',
        },
      }}
      footer={
        <Button type="link" className="link-button" onClick={validateComplexity}>
          {intl.formatMessage({ id: 'salesCodeComplexity.validateComplexity' })}
        </Button>
      }
    >
      <Flex vertical gap={16}>
        <Flex align="center" justify="space-between" wrap>
          <Typography.Text strong>
            {salesCodeState?.showDetailsPopup?.harness || salesCodeState?.showDetailsPopup?.name}
          </Typography.Text>
          <Space size="middle">
            <Button
              type="link"
              className="link-button"
              onClick={async () => {
                const saved = await downloadFile(
                  `${salesCodeState?.showDetailsPopup?.file}?timestamp=${Number(new Date())}`,
                  salesCodeState?.showDetailsPopup?.name
                )
                if (!saved) {
                  notifyError(dispatch, intl.formatMessage({ id: 'salesCodeComplexity.downloadError' }))
                }
              }}
            >
              {intl.formatMessage({ id: 'salesCodeComplexity.downloadSourceFile' })}
            </Button>
            {salesCodeDetails?.logs?.error?.length > 0 && (
              <Button
                type="link"
                className="link-button"
                onClick={() => downloadErrorLogs(salesCodeDetails?.logs?.error)}
              >
                {intl.formatMessage({ id: 'salesCodeComplexity.downloadErrors' })}
              </Button>
            )}
          </Space>
        </Flex>

        <Table
          rowKey="key"
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          size="small"
          scroll={{
            y: '55vh'
          }}
          locale={{
            emptyText: (
              <Empty description={<RecordsCount count={0} />} />
            ),
          }}
        />
        
        {contextHolder}
      </Flex>
    </Modal>
  )
}

export default SalesCodeDetails
